// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: create or edit an Outlook event — title, all-day, times, location,
// description, attendees, an optional Teams meeting and/or a Konvo
// voice-channel join link.
//
// Editing loads the event's full detail first (the calendar list payload has no
// attendees or body). Only the fields the user actually changed are sent, so
// editing a title never rewrites a Teams-generated invite body.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Channels from '@app/features/channel/state/Channels';
import GuildGuilds from '@app/features/guild/state/Guilds';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Activity from '@app/features/konvo_activity/state/Activity';
import styles from '@app/features/konvo_calendar/components/KonvoCreateEventModal.module.css';
import {useAttendeeAvailability} from '@app/features/konvo_calendar/hooks/useAttendeeAvailability';
import KonvoCalendar, {type KonvoCalendarEventDetail} from '@app/features/konvo_calendar/state/KonvoCalendar';
import {
	allDayEndToInclusive,
	channelKeyFromJoinUrl,
	endAfterStartChange,
	eventTimesForRequest,
	isPublicMeetingUrl,
	isValidEventTimeRange,
	toLocalInput,
} from '@app/features/konvo_calendar/utils/KonvoEventForm';
import type {KonvoCalendarCreatePrefill} from '@app/features/konvo_calendar/utils/KonvoMeetingTimes';
import {
	buildRecurrence,
	choiceFromRecurrence,
	dateOnly,
	defaultRecurrenceChoice,
	isSeriesEvent,
	RECURRENCE_WEEKDAYS,
	type RecurrenceChoice,
	type RecurrencePreset,
	type RecurrenceRangeType,
	type RecurrenceWeekday,
	sameRecurrence,
} from '@app/features/konvo_calendar/utils/KonvoRecurrence';
import {RecipientField} from '@app/features/konvo_mail/components/RecipientField';
import type {KonvoMailAddress} from '@app/features/konvo_mail/state/KonvoMail';
import Permission from '@app/features/permissions/state/Permission';
import {http} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Combobox, type ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {Input, Textarea} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {Spinner} from '@app/features/ui/components/Spinner';
import {CheckCircleIcon, GlobeIcon, LinkBreakIcon, SpeakerHighIcon} from '@app/features/ui/icons/Icons';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import * as FormUtils from '@app/lib/forms';
import konvoMarkUrl from '@app/media/images/konvo-logo-flat.svg';
import {ChannelTypes, Permissions} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';

const MEETING_LINK_DESCRIPTOR = msg({message: 'Meeting link', comment: 'Card title in the event form.'});
const WHEN_DESCRIPTOR = msg({message: 'When', comment: 'Card title in the event form.'});
const PEOPLE_DESCRIPTOR = msg({message: 'People', comment: 'Card title in the event form.'});
const ORGANISER_DESCRIPTOR = msg({
	message: 'You are the organiser',
	comment: 'Note in the People card of the event form.',
});
const ADD_DESCRIPTOR = msg({
	message: '+ Add',
	comment: 'Opens an optional card (location, description) in the event form.',
});
const REMOVE_DESCRIPTOR = msg({message: 'Remove', comment: 'Clears and closes an optional card in the event form.'});
const EVERYONE_DESCRIPTOR = msg({
	message: 'Everyone in this chat ({count})',
	comment: 'Chip in the event form that adds all chat participants as attendees.',
});
const ADD_BY_NAME_DESCRIPTOR = msg({message: 'Required · add by name or email', comment: 'Attendee field label.'});
const OPTIONAL_BY_NAME_DESCRIPTOR = msg({message: 'Optional attendees', comment: 'Attendee field label.'});
const KONVO_LINK_DESCRIPTOR = msg({message: 'Konvo', comment: 'Meeting link choice in the event form.'});
const TEAMS_LINK_DESCRIPTOR = msg({message: 'Teams', comment: 'Meeting link choice in the event form.'});
const NO_LINK_DESCRIPTOR = msg({message: 'None', comment: 'Meeting link choice in the event form.'});
const LINK_SELECTION_HINT_DESCRIPTOR = msg({
	message: 'Choose one or both',
	comment: 'Hint explaining that Konvo and Teams meeting links can be enabled together.',
});
const PUBLIC_LINK_DESCRIPTOR = msg({
	message: 'Public link',
	comment: 'Konvo meeting link type in the event form.',
});
const KONVO_LINK_TYPE_DESCRIPTOR = msg({
	message: 'Konvo link type',
	comment: 'Accessible label for the Konvo meeting link type selector.',
});
const CHANNEL_ACCESS_DESCRIPTOR = msg({
	message: 'Members with access',
	comment: 'Short description for a voice-channel meeting link.',
});
const PUBLIC_ACCESS_DESCRIPTOR = msg({
	message: 'No account needed',
	comment: 'Short description for a public meeting link.',
});
const CHECKING_DESCRIPTOR = msg({message: 'Checking calendars…', comment: 'Hint while availability loads.'});
const ALL_FREE_DESCRIPTOR = msg({message: 'Everyone is free at this time.', comment: 'Hint when no attendee clashes.'});
const INVITES_NOTE_DESCRIPTOR = msg({
	message: 'Invites go out from Outlook',
	comment: 'Footer note of the event form.',
});
const CREATE_INVITE_DESCRIPTOR = msg({
	message: 'Create & invite {count}',
	comment: 'Confirm button of the event form; count is the number of attendees.',
});
const REPEAT_CUSTOM_HINT_DESCRIPTOR = msg({
	message: 'every',
	comment: 'Precedes the interval number in the repeat card.',
});
const DURATIONS = [30, 45, 60, 120] as const;
const NEW_EVENT_DESCRIPTOR = msg({
	message: 'New event',
	comment: 'Title of the create-calendar-event modal.',
});
const EVENT_TITLE_DESCRIPTOR = msg({
	message: 'Title',
	comment: 'Field label for the event title.',
});
const STARTS_DESCRIPTOR = msg({
	message: 'Starts',
	comment: 'Field label for the event start time.',
});
const ENDS_DESCRIPTOR = msg({
	message: 'Ends',
	comment: 'Field label for the event end time.',
});
const ALL_DAY_DESCRIPTOR = msg({
	message: 'All day',
	comment: 'Toggle that makes the event span whole days instead of a time range.',
});
const LOCATION_DESCRIPTOR = msg({
	message: 'Location',
	comment: 'Field label for the event location.',
});
const DESCRIPTION_DESCRIPTOR = msg({
	message: 'Description',
	comment: 'Field label for the event description / body.',
});
const VOICE_CHANNEL_DESCRIPTOR = msg({
	message: 'Voice channel',
	comment: 'Field label for the Konvo voice channel picker.',
});
const CHOOSE_CHANNEL_DESCRIPTOR = msg({
	message: 'Choose a channel',
	comment: 'Field label shown beneath the selected voice-channel meeting link type.',
});
const LINKED_CHANNEL_DESCRIPTOR = msg({
	message: 'Linked voice channel',
	comment: 'Placeholder option for a Konvo channel link that is not in the current channel list.',
});
const CREATE_DESCRIPTOR = msg({
	message: 'Create event',
	comment: 'Confirm button in the create-event modal.',
});
const EVENT_CREATED_DESCRIPTOR = msg({
	message: 'Event created in your Outlook calendar.',
	comment: 'Success toast after creating a calendar event.',
});
const INVALID_TIME_RANGE_DESCRIPTOR = msg({
	message: 'The event must end after it starts.',
	comment: 'Validation error shown when a calendar event ends before it starts.',
});
const EDIT_EVENT_DESCRIPTOR = msg({
	message: 'Edit event',
	comment: 'Title of the edit-calendar-event modal.',
});
const SAVE_DESCRIPTOR = msg({
	message: 'Save changes',
	comment: 'Confirm button in the edit-event modal.',
});
const EVENT_UPDATED_DESCRIPTOR = msg({
	message: 'Event updated in your Outlook calendar.',
	comment: 'Success toast after editing a calendar event.',
});
const DETAIL_FAILED_DESCRIPTOR = msg({
	message: 'Could not load the full event, so only the title and times can be changed here.',
	comment: 'Warning shown when the edit form cannot fetch attendees and the description.',
});
const REPEATS_DESCRIPTOR = msg({message: 'Repeats', comment: 'Label of the recurrence picker in the event form.'});
const REPEAT_NONE_DESCRIPTOR = msg({message: 'Does not repeat', comment: 'Recurrence option: one-off event.'});
const REPEAT_DAILY_DESCRIPTOR = msg({message: 'Every day', comment: 'Recurrence option.'});
const REPEAT_WEEKDAYS_DESCRIPTOR = msg({message: 'Every weekday', comment: 'Recurrence option: Monday to Friday.'});
const REPEAT_WEEKLY_DESCRIPTOR = msg({
	message: 'Every week',
	comment: 'Recurrence option; the days are chosen below it.',
});
const REPEAT_MONTHLY_DESCRIPTOR = msg({message: 'Every month', comment: 'Recurrence option: same day each month.'});
const REPEAT_YEARLY_DESCRIPTOR = msg({message: 'Every year', comment: 'Recurrence option: same date each year.'});
const UNIT_DAYS_DESCRIPTOR = msg({message: 'days', comment: 'Unit after the recurrence interval number.'});
const UNIT_WEEKS_DESCRIPTOR = msg({message: 'weeks', comment: 'Unit after the recurrence interval number.'});
const UNIT_MONTHS_DESCRIPTOR = msg({message: 'months', comment: 'Unit after the recurrence interval number.'});
const UNIT_YEARS_DESCRIPTOR = msg({message: 'years', comment: 'Unit after the recurrence interval number.'});
const REPEAT_ENDS_DESCRIPTOR = msg({message: 'Ends', comment: 'Label of the recurrence range choice.'});
const ENDS_NEVER_DESCRIPTOR = msg({message: 'Never', comment: 'Recurrence range option: no end.'});
const ENDS_ON_DATE_DESCRIPTOR = msg({message: 'On a date', comment: 'Recurrence range option: ends on a date.'});
const ENDS_AFTER_DESCRIPTOR = msg({message: 'After a number of times', comment: 'Recurrence range option.'});
const END_DATE_DESCRIPTOR = msg({message: 'Last day', comment: 'Label of the recurrence end date field.'});
const OCCURRENCES_DESCRIPTOR = msg({message: 'Number of times', comment: 'Label of the recurrence count field.'});
const APPLY_TO_DESCRIPTOR = msg({
	message: 'Apply changes to',
	comment: 'Label of the choice between editing one occurrence or the whole series.',
});
const THIS_EVENT_ONLY_DESCRIPTOR = msg({
	message: 'This event only',
	comment: 'Edit scope option for a recurring event.',
});
const WHOLE_SERIES_DESCRIPTOR = msg({message: 'The whole series', comment: 'Edit scope option for a recurring event.'});
const SERIES_TIMES_HINT_DESCRIPTOR = msg({
	message: 'Time changes apply to every event in the series; the dates stay as they are.',
	comment: 'Hint under the whole-series option in the event form.',
});
const NOT_ORGANIZER_DESCRIPTOR = msg({
	message: 'You are not the organiser — Outlook may reject changes to this event.',
	comment: 'Warning shown when editing an event organised by somebody else.',
});

interface KonvoCreateEventModalProps {
	onClose: () => void;
	onCreated?: () => void;
	/** When set, the modal edits this existing event. */
	editing?: import('@app/features/konvo_calendar/state/KonvoCalendar').KonvoCalendarEvent;
	/** Suggested meeting details supplied by a /free result link. */
	initial?: KonvoCalendarCreatePrefill;
	/** People from the conversation this came from, offered as one-tap chips. */
	people?: Array<{name: string; address: string}>;
}

const sameAddresses = (a: Array<KonvoMailAddress>, b: Array<KonvoMailAddress>): boolean =>
	a.length === b.length && a.every((entry, index) => entry.address.toLowerCase() === b[index].address.toLowerCase());

export const KonvoCreateEventModal = observer(
	({onClose, onCreated, editing, initial, people}: KonvoCreateEventModalProps) => {
		const {i18n} = useLingui();
		const defaultStart = useMemo(() => {
			const date = new Date(Date.now() + 30 * 60 * 1000);
			date.setMinutes(date.getMinutes() < 30 ? 30 : 60, 0, 0);
			return date;
		}, []);
		const [subject, setSubject] = useState(editing?.subject ?? initial?.subject ?? '');
		const [allDay, setAllDay] = useState(editing?.is_all_day ?? false);
		const [start, setStart] = useState(() =>
			editing?.start
				? toLocalInput(new Date(editing.start))
				: initial?.start
					? toLocalInput(new Date(initial.start))
					: toLocalInput(defaultStart),
		);
		const [end, setEnd] = useState(() =>
			editing?.end
				? editing.is_all_day
					? `${allDayEndToInclusive(editing.end)}T00:00`
					: toLocalInput(new Date(editing.end))
				: initial?.end
					? toLocalInput(new Date(initial.end))
					: toLocalInput(new Date(defaultStart.getTime() + 30 * 60 * 1000)),
		);
		const [location, setLocation] = useState(editing?.location ?? '');
		const [description, setDescription] = useState('');
		const [attendees, setAttendees] = useState<Array<KonvoMailAddress>>(
			() =>
				initial?.attendees.map((address) => ({
					name: people?.find((person) => person.address.toLowerCase() === address.toLowerCase())?.name ?? null,
					address,
				})) ?? [],
		);
		const [optionalAttendees, setOptionalAttendees] = useState<Array<KonvoMailAddress>>([]);
		const [teams, setTeams] = useState(false);
		const [konvo, setKonvo] = useState(false);
		const [channelId, setChannelId] = useState('');
		const [meetingType, setMeetingType] = useState<'channel' | 'public'>('channel');
		// KONVO: recurrence, as the small set of choices the form offers.
		const [recurrenceChoice, setRecurrenceChoice] = useState<RecurrenceChoice>(() =>
			defaultRecurrenceChoice(dateOnly(start)),
		);
		const [scope, setScope] = useState<'occurrence' | 'series'>('occurrence');
		const [submitting, setSubmitting] = useState(false);
		// Edit mode: the detail load decides which fields are safe to send back.
		const [loadingDetail, setLoadingDetail] = useState(editing != null);
		const [detail, setDetail] = useState<KonvoCalendarEventDetail | null>(null);
		const [detailFailed, setDetailFailed] = useState(false);

		useEffect(() => {
			if (!editing) return;
			let cancelled = false;
			void KonvoCalendar.fetchEvent(editing.id).then((loaded) => {
				if (cancelled) return;
				if (!loaded) {
					setDetailFailed(true);
					setLoadingDetail(false);
					return;
				}
				setDetail(loaded);
				setSubject(loaded.subject);
				setAllDay(loaded.is_all_day);
				if (loaded.start) setStart(toLocalInput(new Date(loaded.start)));
				if (loaded.end) {
					setEnd(loaded.is_all_day ? `${allDayEndToInclusive(loaded.end)}T00:00` : toLocalInput(new Date(loaded.end)));
				}
				setLocation(loaded.location ?? '');
				setDescription(loaded.body_text);
				setAttendees(
					loaded.attendees
						.filter((attendee) => attendee.type !== 'optional')
						.map((attendee) => ({name: attendee.name, address: attendee.address})),
				);
				setOptionalAttendees(
					loaded.attendees
						.filter((attendee) => attendee.type === 'optional')
						.map((attendee) => ({name: attendee.name, address: attendee.address})),
				);
				setTeams(loaded.teams_join_url != null);
				setKonvo(loaded.konvo_join_url != null);
				setChannelId(channelKeyFromJoinUrl(loaded.konvo_join_url));
				setMeetingType(isPublicMeetingUrl(loaded.konvo_join_url) ? 'public' : 'channel');
				setRecurrenceChoice(
					choiceFromRecurrence(
						loaded.recurrence ?? null,
						dateOnly(loaded.start ? toLocalInput(new Date(loaded.start)) : start),
					),
				);
				setLoadingDetail(false);
			});
			return () => {
				cancelled = true;
			};
		}, [editing]);

		// Read observable channels on every render so newly loaded guilds appear.
		const voiceChannelOptions: Array<ComboboxOption<string> & {channelName: string; context: string}> = [];
		for (const guild of GuildGuilds.getGuilds()) {
			const channels = Channels.getGuildChannels(guild.id);
			for (const channel of [...channels].sort((a, b) => {
				const parentA = channels.find((item) => item.id === a.parentId);
				const parentB = channels.find((item) => item.id === b.parentId);
				return (parentA?.position ?? -1) - (parentB?.position ?? -1) || (a.position ?? 0) - (b.position ?? 0);
			})) {
				if (
					channel.type !== ChannelTypes.GUILD_VOICE ||
					!Permission.can(Permissions.VIEW_CHANNEL, channel) ||
					!Permission.can(Permissions.CONNECT, channel)
				)
					continue;
				const category = channels.find((item) => item.id === channel.parentId);
				const context = category ? `${guild.name} / ${category.name}` : guild.name;
				voiceChannelOptions.push({
					value: `${guild.id}:${channel.id}`,
					label: `${channel.name} · ${context}`,
					channelName: channel.name ?? '',
					context,
				});
			}
		}
		if (channelId && !voiceChannelOptions.some((option) => option.value === channelId)) {
			voiceChannelOptions.unshift({
				value: channelId,
				label: i18n._(LINKED_CHANNEL_DESCRIPTOR),
				channelName: i18n._(LINKED_CHANNEL_DESCRIPTOR),
				context: '',
			});
		}

		// One source of truth for "what did the user change?" — used by both the
		// dirty check and the PATCH body, so they can never disagree.
		const loadedForm = useMemo(() => {
			if (!detail) return null;
			const byType = (optional: boolean) =>
				detail.attendees
					.filter((attendee) => (attendee.type === 'optional') === optional)
					.map((attendee) => ({name: attendee.name, address: attendee.address}));
			return {
				subject: detail.subject.trim(),
				allDay: detail.is_all_day,
				start: detail.start ? toLocalInput(new Date(detail.start)) : '',
				end: detail.end
					? detail.is_all_day
						? `${allDayEndToInclusive(detail.end)}T00:00`
						: toLocalInput(new Date(detail.end))
					: '',
				location: (detail.location ?? '').trim(),
				description: detail.body_text,
				attendees: byType(false),
				optionalAttendees: byType(true),
				teams: detail.teams_join_url != null,
				channelKey: isPublicMeetingUrl(detail.konvo_join_url) ? 'public' : channelKeyFromJoinUrl(detail.konvo_join_url),
				recurrence: detail.recurrence ?? null,
			};
		}, [detail]);
		const isSeries = isSeriesEvent(detail?.type ?? editing?.type ?? null);
		const isOccurrence = isSeries && detail?.type !== 'seriesMaster';
		// Recurrence can only change on the series itself.
		const recurrenceEditable = !editing || !isOccurrence || scope === 'series';
		const builtRecurrence = useMemo(
			() => buildRecurrence(recurrenceChoice, dateOnly(start)),
			[recurrenceChoice, start],
		);
		const validTimeRange = isValidEventTimeRange(start, end, allDay);

		const changed = useMemo(() => {
			if (!loadedForm) return null;
			return {
				subject: subject.trim() !== loadedForm.subject,
				times: allDay !== loadedForm.allDay || start !== loadedForm.start || end !== loadedForm.end,
				location: location.trim() !== loadedForm.location,
				description: description !== loadedForm.description,
				attendees:
					!sameAddresses(attendees, loadedForm.attendees) ||
					!sameAddresses(optionalAttendees, loadedForm.optionalAttendees),
				teams: teams !== loadedForm.teams,
				konvo: (konvo ? (meetingType === 'public' ? 'public' : channelId) : '') !== loadedForm.channelKey,
				recurrence: recurrenceEditable && !sameRecurrence(builtRecurrence, loadedForm.recurrence),
			};
		}, [
			recurrenceEditable,
			builtRecurrence,
			loadedForm,
			subject,
			allDay,
			start,
			end,
			location,
			description,
			attendees,
			optionalAttendees,
			teams,
			konvo,
			channelId,
			meetingType,
		]);

		const handleSubmit = useCallback(async () => {
			if (!subject.trim() || !start || !end || !validTimeRange) {
				return;
			}
			const times = eventTimesForRequest(start, end, allDay);
			const [guildId, konvoChannelId] =
				meetingType === 'channel' && channelId.includes(':') ? channelId.split(':') : [null, null];
			const channelLabel = voiceChannelOptions.find((option) => option.value === channelId)?.label ?? null;
			setSubmitting(true);
			try {
				if (editing) {
					// Send only what moved: every PATCH on a meeting mails the attendees
					// an update, and rewriting the body costs the invite's original HTML.
					const body: Record<string, unknown> = {timezone: KonvoCalendar.timezone};
					if (!changed) {
						body['subject'] = subject.trim();
						body['start'] = times.start;
						body['end'] = times.end;
						body['is_all_day'] = allDay;
					} else {
						if (changed.subject) body['subject'] = subject.trim();
						if (changed.times) {
							body['start'] = times.start;
							body['end'] = times.end;
							body['is_all_day'] = allDay;
						}
						if (changed.location) body['location'] = location.trim();
						if (changed.description) body['body_text'] = description;
						if (changed.attendees) {
							body['attendees'] = attendees.map((entry) => entry.address);
							body['optional_attendees'] = optionalAttendees.map((entry) => entry.address);
						}
						if (changed.teams) body['teams'] = teams;
						if (changed.konvo) {
							body['konvo_public'] = konvo && meetingType === 'public';
							body['konvo_channel_id'] = konvo && konvoChannelId ? konvoChannelId : null;
							body['konvo_guild_id'] = konvo && guildId ? guildId : null;
							body['konvo_channel_name'] = konvo && meetingType === 'channel' ? channelLabel : null;
						}
						if (changed.recurrence) body['recurrence'] = builtRecurrence;
					}
					if (isOccurrence) body['scope'] = scope;
					await http.patch(Endpoints.KONVO_CALENDAR_EVENT(editing.id), {body});
					ToastCommands.createToast({type: 'success', children: i18n._(EVENT_UPDATED_DESCRIPTOR)});
					onCreated?.();
					onClose();
					return;
				}
				await http.post(Endpoints.KONVO_CALENDAR_EVENTS, {
					body: {
						subject: subject.trim(),
						start: times.start,
						end: times.end,
						is_all_day: allDay,
						timezone: KonvoCalendar.timezone,
						location: location.trim(),
						body_text: description,
						attendees: attendees.map((entry) => entry.address),
						optional_attendees: optionalAttendees.map((entry) => entry.address),
						teams,
						konvo_public: konvo && meetingType === 'public',
						konvo_channel_id: konvo && konvoChannelId ? konvoChannelId : null,
						konvo_guild_id: konvo && guildId ? guildId : null,
						konvo_channel_name: konvo && meetingType === 'channel' ? channelLabel : null,
						recurrence: builtRecurrence,
					},
				});
				ToastCommands.createToast({type: 'success', children: i18n._(EVENT_CREATED_DESCRIPTOR)});
				Activity.track('action.done', {action: 'event.create', origin: 'konvo'});
				onCreated?.();
				onClose();
			} catch (error) {
				ToastCommands.createToast({
					type: 'error',
					children: FormUtils.extractErrorMessage(i18n, error),
				});
			} finally {
				setSubmitting(false);
			}
		}, [
			subject,
			start,
			end,
			allDay,
			location,
			description,
			attendees,
			optionalAttendees,
			teams,
			konvo,
			channelId,
			meetingType,
			voiceChannelOptions,
			i18n,
			onClose,
			onCreated,
			editing,
			changed,
			builtRecurrence,
			isOccurrence,
			scope,
			validTimeRange,
		]);

		const showAllFields = !editing || detail != null;
		const repeatOptions = useMemo(
			(): Array<ComboboxOption<RecurrencePreset>> => [
				{value: 'none', label: i18n._(REPEAT_NONE_DESCRIPTOR)},
				{value: 'daily', label: i18n._(REPEAT_DAILY_DESCRIPTOR)},
				{value: 'weekdays', label: i18n._(REPEAT_WEEKDAYS_DESCRIPTOR)},
				{value: 'weekly', label: i18n._(REPEAT_WEEKLY_DESCRIPTOR)},
				{value: 'monthly', label: i18n._(REPEAT_MONTHLY_DESCRIPTOR)},
				{value: 'yearly', label: i18n._(REPEAT_YEARLY_DESCRIPTOR)},
			],
			[i18n],
		);
		const rangeOptions = useMemo(
			(): ReadonlyArray<RadioOption<RecurrenceRangeType>> => [
				{value: 'noEnd', name: i18n._(ENDS_NEVER_DESCRIPTOR)},
				{value: 'endDate', name: i18n._(ENDS_ON_DATE_DESCRIPTOR)},
				{value: 'numbered', name: i18n._(ENDS_AFTER_DESCRIPTOR)},
			],
			[i18n],
		);
		const scopeOptions = useMemo(
			(): ReadonlyArray<RadioOption<'occurrence' | 'series'>> => [
				{value: 'occurrence', name: i18n._(THIS_EVENT_ONLY_DESCRIPTOR)},
				{value: 'series', name: i18n._(WHOLE_SERIES_DESCRIPTOR), desc: i18n._(SERIES_TIMES_HINT_DESCRIPTOR)},
			],
			[i18n],
		);
		const weekdayLabel = useMemo(() => {
			const formatter = new Intl.DateTimeFormat(i18n.locale, {weekday: 'short'});
			const monday = new Date(2026, 8, 7, 12);
			const labels = new Map<RecurrenceWeekday, string>();
			RECURRENCE_WEEKDAYS.forEach((weekday, index) => {
				labels.set(weekday, formatter.format(new Date(monday.getTime() + index * 86_400_000)));
			});
			return (weekday: RecurrenceWeekday) => labels.get(weekday) ?? weekday;
		}, [i18n.locale]);
		const intervalUnit =
			recurrenceChoice.preset === 'daily'
				? i18n._(UNIT_DAYS_DESCRIPTOR)
				: recurrenceChoice.preset === 'weekly'
					? i18n._(UNIT_WEEKS_DESCRIPTOR)
					: recurrenceChoice.preset === 'monthly'
						? i18n._(UNIT_MONTHS_DESCRIPTOR)
						: i18n._(UNIT_YEARS_DESCRIPTOR);
		const updateChoice = (patch: Partial<RecurrenceChoice>) =>
			setRecurrenceChoice((current) => ({...current, ...patch}));
		const updateStart = (nextStart: string) => {
			setEnd((currentEnd) => endAfterStartChange(start, nextStart, currentEnd, allDay));
			setStart(nextStart);
		};
		const toggleWeekday = (weekday: RecurrenceWeekday) =>
			setRecurrenceChoice((current) => {
				const has = current.daysOfWeek.includes(weekday);
				const next = has ? current.daysOfWeek.filter((day) => day !== weekday) : [...current.daysOfWeek, weekday];
				return {...current, daysOfWeek: next.length > 0 ? next : current.daysOfWeek};
			});
		const dirty = !editing || changed == null || Object.values(changed).some(Boolean);

		// Optional cards start collapsed unless they already hold something.
		const [locationOpen, setLocationOpen] = useState(false);
		const [descriptionOpen, setDescriptionOpen] = useState(false);
		const [repeatOpen, setRepeatOpen] = useState(false);
		const showLocation = locationOpen || location.trim() !== '';
		const showDescription = descriptionOpen || description !== '';
		const showRepeat = repeatOpen || recurrenceChoice.preset !== 'none';
		const availability = useAttendeeAvailability(
			i18n,
			[...attendees, ...optionalAttendees],
			start,
			end,
			!allDay && validTimeRange && (!editing || detail != null),
			detail ? {...detail, start: editing?.start ?? detail.start, end: editing?.end ?? detail.end} : null,
		);
		const durationMinutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000);
		const setDuration = (minutes: number) =>
			setEnd(toLocalInput(new Date(new Date(start).getTime() + minutes * 60_000)));
		const startDate = start.slice(0, 10);
		const startTime = start.slice(11, 16);
		const endDate = end.slice(0, 10);
		const endTime = end.slice(11, 16);
		const hasPerson = (address: string) =>
			[...attendees, ...optionalAttendees].some((entry) => entry.address.toLowerCase() === address.toLowerCase());
		const togglePerson = (person: {name: string; address: string}) => {
			if (hasPerson(person.address)) {
				setAttendees((current) =>
					current.filter((entry) => entry.address.toLowerCase() !== person.address.toLowerCase()),
				);
				setOptionalAttendees((current) =>
					current.filter((entry) => entry.address.toLowerCase() !== person.address.toLowerCase()),
				);
			} else setAttendees((current) => [...current, {name: person.name, address: person.address}]);
		};
		const everyoneAdded = (people ?? []).every((person) => hasPerson(person.address));
		const initialsOf = (name: string) =>
			name
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
				.map((part) => part[0]?.toUpperCase() ?? '')
				.join('');
		const inviteCount = attendees.length + optionalAttendees.length;
		const submitDisabled =
			loadingDetail ||
			!subject.trim() ||
			!start ||
			!end ||
			!validTimeRange ||
			(showAllFields && konvo && meetingType === 'channel' && !channelId) ||
			(recurrenceChoice.preset !== 'none' && recurrenceChoice.rangeType === 'endDate' && !recurrenceChoice.endDate) ||
			!dirty;

		return (
			<Modal.Root
				size="large"
				centered
				className={styles.modalRoot}
				onClose={onClose}
				data-flx="konvo-calendar.create-event-modal.modal-root"
			>
				<Modal.Header
					title={i18n._(editing ? EDIT_EVENT_DESCRIPTOR : NEW_EVENT_DESCRIPTOR)}
					data-flx="konvo-calendar.create-event-modal.modal-header"
				/>
				<Modal.Content data-flx="konvo-calendar.create-event-modal.modal-content">
					<Modal.ContentLayout data-flx="konvo-calendar.create-event-modal.modal-content-layout">
						{loadingDetail ? (
							<div style={{display: 'flex', justifyContent: 'center', padding: '40px 0'}}>
								<Spinner data-flx="konvo-calendar.create-event-modal.spinner" />
							</div>
						) : (
							<div className={styles.cols} data-flx="konvo-calendar.create-event-modal.columns">
								<div>
									{detailFailed && <div className={styles.notice}>{i18n._(DETAIL_FAILED_DESCRIPTOR)}</div>}
									{detail?.is_organizer === false && (
										<div className={styles.notice}>{i18n._(NOT_ORGANIZER_DESCRIPTOR)}</div>
									)}
									<input
										id="konvo-event-title"
										className={styles.title}
										value={subject}
										placeholder={i18n._(EVENT_TITLE_DESCRIPTOR)}
										aria-label={i18n._(EVENT_TITLE_DESCRIPTOR)}
										onChange={(event) => setSubject(event.target.value)}
										data-flx="konvo-calendar.create-event-modal.title"
									/>

									<section className={styles.card} data-flx="konvo-calendar.create-event-modal.when">
										<div className={styles.cardHead}>
											{i18n._(WHEN_DESCRIPTOR)}
											<span className={styles.allDay}>
												<Switch
													label={i18n._(ALL_DAY_DESCRIPTOR)}
													value={allDay}
													onChange={setAllDay}
													data-flx="konvo-calendar.create-event-modal.all-day-toggle"
												/>
											</span>
										</div>
										<div className={styles.when}>
											<input
												type="date"
												value={startDate}
												aria-label={i18n._(STARTS_DESCRIPTOR)}
												onChange={(event) => updateStart(`${event.target.value}T${allDay ? '00:00' : startTime}`)}
												data-flx="konvo-calendar.create-event-modal.start"
											/>
											{!allDay && (
												<input
													type="time"
													value={startTime}
													aria-label={i18n._(STARTS_DESCRIPTOR)}
													onChange={(event) => updateStart(`${startDate}T${event.target.value}`)}
													data-flx="konvo-calendar.create-event-modal.start-time"
												/>
											)}
											<span>–</span>
											{allDay ? (
												<input
													type="date"
													value={endDate}
													min={startDate}
													aria-label={i18n._(ENDS_DESCRIPTOR)}
													onChange={(event) => setEnd(`${event.target.value}T00:00`)}
													data-flx="konvo-calendar.create-event-modal.end"
												/>
											) : (
												<>
													<input
														type="time"
														value={endTime}
														aria-label={i18n._(ENDS_DESCRIPTOR)}
														onChange={(event) => setEnd(`${endDate}T${event.target.value}`)}
														data-flx="konvo-calendar.create-event-modal.end"
													/>
													{endDate !== startDate && (
														<input
															type="date"
															value={endDate}
															min={startDate}
															aria-label={i18n._(ENDS_DESCRIPTOR)}
															onChange={(event) => setEnd(`${event.target.value}T${endTime}`)}
															data-flx="konvo-calendar.create-event-modal.end-date"
														/>
													)}
													<span className={styles.durations}>
														{DURATIONS.map((minutes) => (
															<button
																key={minutes}
																type="button"
																className={clsx(durationMinutes === minutes && styles.on)}
																onClick={() => setDuration(minutes)}
																data-flx="konvo-calendar.create-event-modal.duration"
															>
																{minutes < 60 ? `${minutes}m` : `${minutes / 60}h`}
															</button>
														))}
													</span>
												</>
											)}
										</div>
										{start && end && !validTimeRange && (
											<div className={clsx(styles.free, styles.busy)}>{i18n._(INVALID_TIME_RANGE_DESCRIPTOR)}</div>
										)}
										{availability.conflicts.length > 0 ? (
											<ul className={styles.conflicts}>
												{availability.conflicts.map((conflict) => (
													<li key={conflict.address} className={styles[conflict.status]}>
														{conflict.text}
													</li>
												))}
											</ul>
										) : availability.checking ? (
											<div className={clsx(styles.free, styles.quiet)}>{i18n._(CHECKING_DESCRIPTOR)}</div>
										) : availability.checked ? (
											<div className={styles.free}>{i18n._(ALL_FREE_DESCRIPTOR)}</div>
										) : null}
										{showAllFields && isOccurrence && (
											<div style={{marginTop: 10}}>
												<RadioGroup
													options={scopeOptions}
													value={scope}
													onChange={setScope}
													aria-label={i18n._(APPLY_TO_DESCRIPTOR)}
													data-flx="konvo-calendar.create-event-modal.scope"
												/>
											</div>
										)}
									</section>

									{showAllFields && (
										<section className={styles.card} data-flx="konvo-calendar.create-event-modal.people">
											<div className={styles.cardHead}>
												{i18n._(PEOPLE_DESCRIPTOR)}
												<span className={styles.cardNote}>{i18n._(ORGANISER_DESCRIPTOR)}</span>
											</div>
											{people && people.length > 0 && (
												<div className={styles.chips}>
													{people.map((person) => {
														const on = hasPerson(person.address);
														const status = on ? availability.statusFor(person.address) : null;
														return (
															<button
																key={person.address}
																type="button"
																className={clsx(styles.chip, on && styles.on)}
																onClick={() => togglePerson(person)}
																aria-pressed={on}
																data-flx="konvo-calendar.create-event-modal.person"
															>
																<span className={styles.avatar}>{initialsOf(person.name)}</span>
																{person.name}
																{(status === 'busy' || status === 'tentative') && (
																	<span className={clsx(styles.dot, styles[status])} />
																)}
																<span className={styles.mark}>{on ? '✓' : '+'}</span>
															</button>
														);
													})}
													{people.length > 1 && !everyoneAdded && (
														<button
															type="button"
															className={clsx(styles.chip, styles.all)}
															onClick={() =>
																setAttendees((current) => [
																	...current,
																	...people
																		.filter((person) => !hasPerson(person.address))
																		.map((person) => ({name: person.name, address: person.address})),
																])
															}
															data-flx="konvo-calendar.create-event-modal.everyone"
														>
															{i18n._(EVERYONE_DESCRIPTOR, {count: people.length})}
														</button>
													)}
												</div>
											)}
											<div className={styles.stack}>
												<RecipientField
													label={i18n._(ADD_BY_NAME_DESCRIPTOR)}
													value={attendees}
													onChange={setAttendees}
												/>
												<RecipientField
													label={i18n._(OPTIONAL_BY_NAME_DESCRIPTOR)}
													value={optionalAttendees}
													onChange={setOptionalAttendees}
												/>
											</div>
										</section>
									)}
								</div>

								<div>
									{showAllFields && (
										<section className={styles.card} data-flx="konvo-calendar.create-event-modal.link">
											<div className={styles.cardHead}>
												{i18n._(MEETING_LINK_DESCRIPTOR)}
												<span className={styles.cardNote}>{i18n._(LINK_SELECTION_HINT_DESCRIPTOR)}</span>
											</div>
											<div className={styles.providers} role="group" aria-label={i18n._(MEETING_LINK_DESCRIPTOR)}>
												<button
													type="button"
													className={clsx(styles.providerButton, konvo && styles.on)}
													aria-pressed={konvo}
													onClick={() => setKonvo((value) => !value)}
													data-flx="konvo-calendar.create-event-modal.konvo-toggle"
												>
													<img src={konvoMarkUrl} alt="" width={14} height={14} />
													<span className={styles.providerName}>{i18n._(KONVO_LINK_DESCRIPTOR)}</span>
													<span className={styles.providerIndicator}>
														{konvo && <CheckCircleIcon size={16} weight="fill" />}
													</span>
												</button>
												<button
													type="button"
													className={clsx(styles.providerButton, teams && styles.on)}
													aria-pressed={teams}
													onClick={() => setTeams((value) => !value)}
													data-flx="konvo-calendar.create-event-modal.teams-toggle"
												>
													<TeamsLogo size={14} />
													<span className={styles.providerName}>{i18n._(TEAMS_LINK_DESCRIPTOR)}</span>
													<span className={styles.providerIndicator}>
														{teams && <CheckCircleIcon size={16} weight="fill" />}
													</span>
												</button>
												<button
													type="button"
													className={clsx(styles.providerButton, !konvo && !teams && styles.on)}
													aria-pressed={!konvo && !teams}
													onClick={() => {
														setKonvo(false);
														setTeams(false);
													}}
													data-flx="konvo-calendar.create-event-modal.no-link"
												>
													<LinkBreakIcon size={15} />
													<span className={styles.providerName}>{i18n._(NO_LINK_DESCRIPTOR)}</span>
													<span className={styles.providerIndicator}>
														{!konvo && !teams && <CheckCircleIcon size={16} weight="fill" />}
													</span>
												</button>
											</div>
											{konvo && (
												<div className={styles.linkType} role="group" aria-label={i18n._(KONVO_LINK_TYPE_DESCRIPTOR)}>
													<button
														type="button"
														className={clsx(meetingType === 'channel' && styles.on)}
														aria-pressed={meetingType === 'channel'}
														onClick={() => setMeetingType('channel')}
														data-flx="konvo-calendar.create-event-modal.channel-link-type"
													>
														<SpeakerHighIcon size={17} />
														<span>
															<strong>{i18n._(VOICE_CHANNEL_DESCRIPTOR)}</strong>
															<small>{i18n._(CHANNEL_ACCESS_DESCRIPTOR)}</small>
														</span>
													</button>
													<button
														type="button"
														className={clsx(meetingType === 'public' && styles.on)}
														aria-pressed={meetingType === 'public'}
														onClick={() => setMeetingType('public')}
														data-flx="konvo-calendar.create-event-modal.public"
													>
														<GlobeIcon size={17} />
														<span>
															<strong>{i18n._(PUBLIC_LINK_DESCRIPTOR)}</strong>
															<small>{i18n._(PUBLIC_ACCESS_DESCRIPTOR)}</small>
														</span>
													</button>
												</div>
											)}
											{konvo && meetingType === 'channel' && (
												<div style={{marginTop: 10}}>
													<Combobox
														id="konvo-event-channel"
														label={i18n._(CHOOSE_CHANNEL_DESCRIPTOR)}
														value={channelId}
														options={voiceChannelOptions}
														placeholder={i18n._(msg`Search channels, categories or teams`)}
														wrapMenuText
														renderOption={(option) => (
															<span style={{display: 'flex', flexDirection: 'column', gap: 3}}>
																<span>{option.channelName}</span>
																<span style={{fontSize: 12, color: 'var(--text-secondary)'}}>{option.context}</span>
															</span>
														)}
														onChange={setChannelId}
														data-flx="konvo-calendar.create-event-modal.channel"
													/>
												</div>
											)}
											{konvo && meetingType === 'public' && (
												<p className={styles.publicLinkHint}>
													{i18n._(
														msg`Anyone with the link can join. It expires 24 hours after the event or final occurrence ends; ongoing series stay active until cancelled.`,
													)}
												</p>
											)}
										</section>
									)}

									{showAllFields && (
										<section
											className={clsx(styles.card, styles.optional, !showLocation && styles.closed)}
											data-flx="konvo-calendar.create-event-modal.location-card"
										>
											<div className={styles.cardHead}>
												{i18n._(LOCATION_DESCRIPTOR)}
												<button
													type="button"
													className={styles.cardAction}
													onClick={() => {
														if (showLocation) setLocation('');
														setLocationOpen(!showLocation);
													}}
												>
													{i18n._(showLocation ? REMOVE_DESCRIPTOR : ADD_DESCRIPTOR)}
												</button>
											</div>
											{showLocation && (
												<Input
													id="konvo-event-location"
													type="text"
													value={location}
													autoFocus={locationOpen}
													onChange={(event) => setLocation(event.target.value)}
													data-flx="konvo-calendar.create-event-modal.location"
												/>
											)}
										</section>
									)}

									{showAllFields && (
										<section
											className={clsx(styles.card, styles.optional, !showDescription && styles.closed)}
											data-flx="konvo-calendar.create-event-modal.description-card"
										>
											<div className={styles.cardHead}>
												{i18n._(DESCRIPTION_DESCRIPTOR)}
												<button
													type="button"
													className={styles.cardAction}
													onClick={() => {
														if (showDescription) setDescription('');
														setDescriptionOpen(!showDescription);
													}}
												>
													{i18n._(showDescription ? REMOVE_DESCRIPTOR : ADD_DESCRIPTOR)}
												</button>
											</div>
											{showDescription && (
												<Textarea
													id="konvo-event-description"
													label=""
													value={description}
													minRows={3}
													maxRows={8}
													autoFocus={descriptionOpen}
													onChange={(event) => setDescription(event.target.value)}
													data-flx="konvo-calendar.create-event-modal.description"
												/>
											)}
										</section>
									)}

									{showAllFields && recurrenceEditable && (
										<section
											className={clsx(styles.card, styles.optional, !showRepeat && styles.closed)}
											data-flx="konvo-calendar.create-event-modal.repeat-card"
										>
											<div className={styles.cardHead}>
												{i18n._(REPEATS_DESCRIPTOR)}
												<button
													type="button"
													className={styles.cardAction}
													onClick={() => {
														if (showRepeat) updateChoice({preset: 'none'});
														setRepeatOpen(!showRepeat);
													}}
												>
													{showRepeat ? i18n._(REMOVE_DESCRIPTOR) : `${i18n._(REPEAT_NONE_DESCRIPTOR)} ▾`}
												</button>
											</div>
											{showRepeat && (
												<div className={styles.stack}>
													<div className={styles.chips} style={{marginBottom: 0}}>
														{repeatOptions
															.filter((option) => option.value !== 'none')
															.map((option) => (
																<button
																	key={option.value}
																	type="button"
																	className={clsx(styles.chip, recurrenceChoice.preset === option.value && styles.on)}
																	style={{paddingInlineStart: 10}}
																	onClick={() => updateChoice({preset: option.value})}
																	data-flx="konvo-calendar.create-event-modal.repeats"
																>
																	{option.label}
																</button>
															))}
													</div>
													{recurrenceChoice.preset !== 'none' && recurrenceChoice.preset !== 'weekdays' && (
														<label className={styles.interval}>
															{i18n._(REPEAT_CUSTOM_HINT_DESCRIPTOR)}
															<input
																type="number"
																min={1}
																max={99}
																value={String(recurrenceChoice.interval)}
																onChange={(event) => updateChoice({interval: Number(event.target.value) || 1})}
																data-flx="konvo-calendar.create-event-modal.repeat-interval"
															/>
															{intervalUnit}
														</label>
													)}
													{recurrenceChoice.preset === 'weekly' && (
														<div
															className={styles.chips}
															style={{marginBottom: 0}}
															data-flx="konvo-calendar.create-event-modal.weekdays"
														>
															{RECURRENCE_WEEKDAYS.map((weekday) => {
																const on = recurrenceChoice.daysOfWeek.includes(weekday);
																return (
																	<button
																		key={weekday}
																		type="button"
																		className={clsx(styles.weekday, on && styles.on)}
																		onClick={() => toggleWeekday(weekday)}
																		aria-pressed={on}
																		data-flx="konvo-calendar.create-event-modal.weekday"
																	>
																		{weekdayLabel(weekday)}
																	</button>
																);
															})}
														</div>
													)}
													{recurrenceChoice.preset !== 'none' && (
														<div className={styles.repeatEnds} data-flx="konvo-calendar.create-event-modal.repeat-ends">
															{i18n._(REPEAT_ENDS_DESCRIPTOR)}
															{rangeOptions.map((option) => (
																<button
																	key={option.value}
																	type="button"
																	className={clsx(recurrenceChoice.rangeType === option.value && styles.on)}
																	onClick={() => updateChoice({rangeType: option.value})}
																>
																	{option.name}
																</button>
															))}
															{recurrenceChoice.rangeType === 'endDate' && (
																<input
																	type="date"
																	className={styles.title}
																	style={{width: 'auto', margin: 0, fontSize: 13, fontWeight: 500}}
																	value={recurrenceChoice.endDate}
																	aria-label={i18n._(END_DATE_DESCRIPTOR)}
																	onChange={(event) => updateChoice({endDate: event.target.value})}
																	data-flx="konvo-calendar.create-event-modal.repeat-until"
																/>
															)}
															{recurrenceChoice.rangeType === 'numbered' && (
																<label className={styles.interval}>
																	<input
																		type="number"
																		min={1}
																		max={999}
																		value={String(recurrenceChoice.occurrences)}
																		aria-label={i18n._(OCCURRENCES_DESCRIPTOR)}
																		onChange={(event) => updateChoice({occurrences: Number(event.target.value) || 1})}
																		data-flx="konvo-calendar.create-event-modal.repeat-count"
																	/>
																</label>
															)}
														</div>
													)}
												</div>
											)}
										</section>
									)}
								</div>
							</div>
						)}
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer data-flx="konvo-calendar.create-event-modal.modal-footer">
					<Button variant="secondary" onClick={onClose} data-flx="konvo-calendar.create-event-modal.button.close">
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					{!editing && <span className={styles.footNote}>{i18n._(INVITES_NOTE_DESCRIPTOR)}</span>}
					<Button
						variant="primary"
						onClick={handleSubmit}
						submitting={submitting}
						disabled={submitDisabled}
						data-flx="konvo-calendar.create-event-modal.button.confirm"
					>
						{editing
							? i18n._(SAVE_DESCRIPTOR)
							: inviteCount > 0
								? i18n._(CREATE_INVITE_DESCRIPTOR, {count: inviteCount})
								: i18n._(CREATE_DESCRIPTOR)}
					</Button>
				</Modal.Footer>
			</Modal.Root>
		);
	},
);
